<template>
    <div v-if="session">
        <div class="sess-grid">
            <div>
                <div class="panel">
                    <h4>What we're reviewing</h4>
                    <textarea class="oc-notes" v-model="session.ReviewingText" :readonly="readOnly" @blur="save()" :placeholder="readOnly ? '' : 'Describe what you\'re training on at the station — what you\'re reviewing with them.'"></textarea>
                    <!-- Viewing only: just the attached processes, each opens the process -->
                    <template v-if="readOnly">
                        <div class="os-proc-lbl">Related processes</div>
                        <div v-if="attachedProcesses.length" class="os-chips">
                            <button v-for="(p, i) in attachedProcesses" :key="i" type="button" class="os-chip on" @click="openProcess(p)">
                                <svg-icon icon="check" />{{ p.Name }}
                            </button>
                        </div>
                        <div v-else style="font-size:13px;color:#a8a4a0">None</div>
                    </template>
                    <div v-else class="os-proc-lbl">Related processes <span style="font-weight:normal">· optional</span></div>
                    <input v-if="!readOnly" type="text" v-model="processSearchQ" placeholder="Search processes" style="width:100%;margin:0 0 10px" />
                    <div v-if="!readOnly" style="max-height:200px;overflow:auto">
                        <div class="os-chips">
                            <button v-for="(p, i) in filteredProcessOptions" :key="i" type="button" :class="'os-chip' + (isProcessAttached(p) ? ' on' : '')" @click="toggleProcess(p)">
                                <svg-icon :icon="isProcessAttached(p) ? 'check' : 'plus'" />{{ p.Name }}
                            </button>
                        </div>
                    </div>
                    <div style="margin-top:25px">
                        <h4 style="margin-bottom:6px">Notes</h4>
                        <textarea class="oc-notes" v-model="session.Notes" :readonly="readOnly" @blur="save()" :placeholder="readOnly ? '' : 'How it went, what to revisit next time…'"></textarea>
                    </div>
                </div>
            </div>
            <div>
                <div v-if="!traineesList.length" class="panel">
                    <h4>Trainees</h4>
                    <div style="padding:26px 12px 26px;text-align:center;font-size:14px">
                        <div><svg-icon icon="twousers" style="width:48px;height:48px;" /></div>
                        <div style="margin-bottom:5px;font-size:18px">No trainees yet.</div>
                        <div v-if="!readOnly" style="margin-bottom:15px">Pick who's being trained at the station.</div>
                        <div v-if="!readOnly">
                            <button @click="() => { showPicker = true }" class="btn-lg" style="margin:0"><svg-icon icon="twousers" style="width:14px;height:14px" />Select trainees</button>
                        </div>
                    </div>
                </div>
                <div v-else class="panel">
                    <h4>Trainees<span>{{ signedOffCount }}/{{ traineesList.length }} signed off</span></h4>
                    <div v-for="(t, i) in traineesList" :key="i" class="att">
                        <div class="av">{{ t.initials }}</div>
                        <div class="att-n">
                            <div style="color:var(--dark-gray)">{{ t.name }}</div>
                            <div v-if="t.signoffStatus == 'Signed off'" class="os-signed"><svg-icon icon="check2" />Signed off by {{ t.signedByName }} · {{ t.signedAtString }}</div>
                            <div v-else-if="t.signoffStatus == 'Needs practice'" class="os-practice"><svg-icon icon="alerttriangle" />Needs more practice</div>
                            <div v-else style="font-size:12px;color:#a8a4a0">Not signed off yet</div>
                        </div>
                        <div v-if="!readOnly" class="os-seg">
                            <button type="button" :class="'os-seg-btn' + (t.signoffStatus == 'Needs practice' ? ' prac' : '')" @click="signoff(t, 'Needs practice')">Needs practice</button>
                            <button type="button" :class="'os-seg-btn' + (t.signoffStatus == 'Signed off' ? ' sign' : '')" @click="signoff(t, 'Signed off')"><svg-icon icon="check" />Sign off</button>
                        </div>
                    </div>
                    <div v-if="!readOnly" style="margin-top:15px">
                        <a @click.prevent="() => { showPicker = true }" class="primary" style="text-decoration:none!important;"><svg-icon icon="adduser" style="width:14px; height:14px" />&nbsp;&nbsp;Edit trainees</a>
                    </div>
                </div>

                <div v-if="!readOnly || session.Status == 'Completed'" class="panel">
                    <button v-if="session.Status != 'Completed'" @click="markComplete()" class="btn-lg" style="width:100%;justify-content:center"><svg-icon icon="check3" style="width:14px;height:14px" />Mark session complete</button>
                    <span v-else class="pill p-done"><svg-icon icon="check2" />Session Complete</span>
                </div>
            </div>
        </div>

        <div v-if="showPicker" class="error-msg-wrap">
            <div class="form-wrapper slide-up">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="() => { showPicker = false }">&times;</button>
                </div>
                <h3>Who's being trained?</h3>
                <div>Select everyone being trained{{ session.Station ? ' at ' : '' }}<b>{{ session.Station }}</b>. They'll be signed off individually as they're ready.</div>
                <input style="margin-top:15px" type="text" v-model="employeeSearchQ" @input="filterEmployees()" placeholder="Search employees" />
                <div style="max-height: 200px; overflow: auto;">
                    <div v-for="(e, i) in employees" :key="i" style="margin-bottom:5px" class="checkbox-input">
                        <input type="checkbox" v-model="e.checked" />
                        <div><span style="font-weight:600">{{ e.Name }} · </span><span style="font-size:14px">{{ e.SeatTitle }}</span></div>
                    </div>
                </div>
                <button style="margin-top:25px;width:100%" :class="disableButton ? 'btn-lg disable-button' : 'btn-lg'" @click="submitPicker()">Done</button>
            </div>
        </div>

        <pdf-pop-up v-if="viewProcess" :url="processSlug" @close="() => viewProcess = false" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import SvgIcon from '@/components/SvgIcon.vue';
import PDFPopUp from './PDFPopUp.vue';

export default defineComponent({
    name: 'OnsiteSession',
    // `classroom` is only used to seed our own local `session` copy (see created()) --
    // everything after that reads/writes `session`, never the prop itself, so we're not
    // mutating a prop in place.
    // readOnly: someone who only views sessions (not a Team Hub admin or manager, Classroom.vue canManage): no editing,
    // just the attached processes to open
    props: [ 'classroom', 'employeesAll', 'processOptions', 'readOnly' ],
    components: {
        SvgIcon, PdfPopUp: PDFPopUp
    },
    data() {
        return {
            session: null as any,
            showPicker: false,
            employees: [] as any,
            employeeSearchQ: '',
            processSearchQ: '',
            disableButton: false,
            traineesList: [] as any,
            viewProcess: false,
            processSlug: null as any
        }
    },
    created() {
        this.session = JSON.parse(JSON.stringify(this.classroom));
        this.employees = this.employeesAll;
        this.buildTraineesList();
    },
    computed: {
        signedOffCount(): number {
            return this.traineesList.filter((t: any) => { return t.signoffStatus == 'Signed off' }).length;
        },
        // The processes attached to the session (what a viewer sees in place of the picker)
        attachedProcesses(): any[] {
            return (this.processOptions as any[] || []).filter((p: any) => this.isProcessAttached(p));
        },
        filteredProcessOptions(): any {
            if(this.processSearchQ.length < 2) {
                return this.processOptions;
            }
            const searchLower = this.processSearchQ.toLowerCase();
            return (this.processOptions as any[]).filter((p: any) => {
                return `${p.Name}`.toLowerCase().includes(searchLower);
            })
        }
    },
    watch: {
        employeesAll() {
            this.employees = this.employeesAll;
        },
        // Classroom.vue applies live updates pushed from other devices to the `classroom` prop
        // (see applyRemoteUpdate() there) -- mirror the fields that matter here (trainee
        // signoffs, attached processes, session status) into our own `session` copy. Freeform
        // text (ReviewingText/Notes) is left alone so we don't clobber whatever's being typed.
        classroom: {
            deep: true,
            handler() {
                this.session.AttendeesJSON = this.classroom.AttendeesJSON;
                this.session.ProcessesJSON = this.classroom.ProcessesJSON;
                this.session.Status = this.classroom.Status;
                this.session.Completed = this.classroom.Completed;
                this.buildTraineesList();
            }
        }
    },
    methods: {
        buildTraineesList() {
            const list = [];
            const attendees = this.session.AttendeesJSON || [];
            for(const a of attendees) {
                const emp = (this.employeesAll as any[]).find((e: any) => {
                    return e.ID == a.id;
                });
                if(!emp) {
                    continue;
                }
                const nameSplit = emp.Name.split(' ');
                let initials = ``;
                if(nameSplit[0]) {
                    initials += nameSplit[0].substring(0, 1);
                }
                if(nameSplit[1]) {
                    initials += nameSplit[1].substring(0, 1);
                }
                list.push({
                    id: emp.ID,
                    name: emp.Name,
                    jobTitle: emp.SeatTitle, // their role sheet seat's title when added (key kept: saved sessions use it)
                    initials: initials.toUpperCase(),
                    signoffStatus: a.signoffStatus || 'None',
                    signedByName: a.signedByName,
                    signedAtString: a.signedAt ? new Date(a.signedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
                })
            }
            this.traineesList = list;
        },
        isProcessAttached(p: any) {
            return (this.session.ProcessesJSON || []).some((pr: any) => { return pr.slug == p.Slug });
        },
        async toggleProcess(p: any) {
            if(this.readOnly) {
                return;
            }
            if(!this.session.ProcessesJSON) {
                this.session.ProcessesJSON = [];
            }
            const i = this.session.ProcessesJSON.findIndex((pr: any) => { return pr.slug == p.Slug });
            if(i >= 0) {
                this.processSlug = '/playbook/' + p.Slug + '?hideNav=t&hideComponent=t&hideChat=t';
                this.viewProcess = true;
            } else {
                this.session.ProcessesJSON.push({ slug: p.Slug });
            }
            await this.save();
        },
        filterEmployees() {
            if(this.employeeSearchQ.length < 3) {
                this.employees = this.employeesAll;
            } else {
                const searchQLower = this.employeeSearchQ.toLowerCase();
                this.employees = (this.employeesAll as any[]).filter((e: any) => {
                    const eSearch = `${e.Name} ${e.SeatTitle || ''}`.toLowerCase();
                    return eSearch.includes(searchQLower);
                })
            }
        },
        async submitPicker() {
            const selectedEmployees = (this.employeesAll as any[]).filter((e: any) => {
                return e.checked;
            })
            if(!selectedEmployees.length) {
                alert('Select at least one trainee');
                return;
            }
            let prevAttendees: any[] = [];
            if(this.session.AttendeesJSON) {
                prevAttendees = JSON.parse(JSON.stringify(this.session.AttendeesJSON));
            }
            this.session.AttendeesJSON = selectedEmployees.map((e: any) => {
                const prevItem = prevAttendees.find((a: any) => { return a.id == e.ID });
                return prevItem || { id: e.ID, signoffStatus: 'None' };
            });
            this.disableButton = true;
            await axios.post(`${this.$store.state.apiUrl}/api/classroom`, this.session)
                .then(resp => {
                    this.showPicker = false;
                    this.buildTraineesList();
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })
            this.disableButton = false;
        },
        openProcess(p: any) {
            this.processSlug = '/playbook/' + p.Slug + '?hideNav=t&hideComponent=t&hideChat=t';
            this.viewProcess = true;
        },
        async save() {
            if(this.readOnly) {
                return;
            }
            await axios.post(`${this.$store.state.apiUrl}/api/classroom`, this.session)
                .catch(e => {
                    console.log(e);
                })
        },
        async signoff(t: any, status: string) {
            if(this.readOnly) {
                return;
            }
            const newStatus = t.signoffStatus == status ? 'None' : status;
            await axios.post(`${this.$store.state.apiUrl}/api/classroom/${this.session.ID}/signoff`, { employeeID: t.id, status: newStatus })
                .then(resp => {
                    this.session.AttendeesJSON = resp.data;
                    this.buildTraineesList();
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })
        },
        async markComplete() {
            if(this.readOnly) {
                return;
            }
            this.session.Status = 'Completed';
            this.session.Completed = true;
            await axios.post(`${this.$store.state.apiUrl}/api/classroom`, this.session)
                .catch(e => {
                    console.log(e);
                })
        }
    }
})
</script>
